/*  iceberg-ui  */
import { Icon, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    DesktopWindows,
    DomainVerification,
    ExtensionOutlined,
    FileOpen,
    Lock,
    People,
    SelectAll,
    SettingsApplications,
    Tab,
    WebhookRounded
} from '@mui/icons-material';
import { Box, Button, Grid, Paper, TableCell, Typography } from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { ManagedTable } from 'application/Shared/Components/Table';
import { FontSizes } from 'application/Shared/Constants';
import { Route, SessionHelper } from 'application/Shared/Helpers';
import { AdminView } from 'application/Shared/Views';
import { useSession } from 'hooks/redux/Selectors/session';

/*  NPM  */
import i18n from 'i18n';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

const headers = [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{i18n.t('Common.name')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'name',
        width: '20%'
    },
    {
        align: 'left',
        field: 'description',
        renderHeader: <strong>{i18n.t('Common.description')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'description',
        width: '65%'
    },
    {
        align: 'right',
        field: 'enabled',
        renderHeader: <strong>{i18n.t('Common.actions')}</strong>,
        sortable: false,
        width: '15%'
    }
];

const searchFields = ['name', 'description'];

export const AdminUtilities = () => {
    const { clientId, partnerId } = useParams();
    const baseAdminRoute = Route.buildBaseAdminRoute({ clientId, partnerId });

    const { t } = useTranslation();

    const session = useSession();
    const sessionHelper = new SessionHelper(session);
    const sessionIsAnm = !sessionHelper.isANM();

    const utilitiesOptions = useMemo(() => {
        let options = [
            {
                // Application Bulk Editor
                action: t('Common.go'),
                description: t('Configure.bulk_editor'),
                disabled: false,
                icon: <SelectAll sx={{ color: 'icon.primary' }} />,
                key: 'bulk_editor_card',
                name: t('Sidebar.bulk_editor'),
                page: `${baseAdminRoute}/bulk`,
                permission: ['system_bulk_editor']
            },
            {
                // Browser Extension Deployment
                action: t('Common.go'),
                description: t('Configure.browser_agent'),
                disabled: false,
                icon: <Tab sx={{ color: 'icon.primary' }} />,
                key: 'browser_agent_card',
                name: t('PartnerDeployment.browser_agent'),
                page: `${baseAdminRoute}/extension`,
                permission: ['partner']
            },
            {
                // Client Settings
                action: t('Common.go'),
                description: t('Configure.client_settings'),
                disabled: false,
                icon: <SettingsApplications sx={{ color: 'icon.primary' }} />,
                key: 'client_settings_card',
                name: t('Sidebar.client_settings'),
                page: `${baseAdminRoute}/clientSettings`,
                permission: 'partner'
            },
            {
                // Deployment Guide
                action: t('Common.go'),
                description: t('Utilities.deployment_guide_description'),
                disabled: false,
                external: true,
                icon: <FileOpen sx={{ color: 'icon.primary' }} />,
                key: 'deployment_guide_card',
                name: t('Utilities.deployment_guide'),
                page: 'https://support.auvik.com/hc/en-us/sections/11423558673172',
                permission: ['partner']
            },
            {
                // Desktop Deployment Scripts
                action: t('Common.go'),
                description: t('Utilities.deployment_scripts_description'),
                disabled: false,
                icon: <DesktopWindows sx={{ color: 'icon.primary' }} />,
                key: 'deployment_scripts_card',
                name: t('Utilities.deployment_scripts'),
                page: `${baseAdminRoute}/identityManagement/deploymentScripts`,
                permission: ['partner']
            },
            {
                // Domain Configuration
                action: t('Common.configure'),
                description: t('Configure.domain_description'),
                disabled: false,
                icon: <DomainVerification sx={{ color: 'icon.primary' }} />,
                key: 'domain_configuration',
                name: t('Common.domain_configuration'),
                page: `${baseAdminRoute}/domains`,
                permission: ['system_integrations']
            },
            {
                // Tag Editor
                action: t('Common.go'),
                description: t('Configure.tag_editor'),
                disabled: false,
                icon: <Icon size={FontSizes[4]} src="Tag1" />,
                key: 'tag_editor_card',
                name: t('Sidebar.tag_editor'),
                page: `${baseAdminRoute}/tags`,
                permission: ['system_bulk_editor']
            },
            {
                // Webhooks
                action: t('Common.configure'),
                description: t('Utilities.Webhooks.description'),
                disabled: false,
                icon: <WebhookRounded sx={{ color: 'icon.primary' }} />,
                key: 'webhooks',
                name: t('Common.webhooks'),
                page: `${baseAdminRoute}/webhooks`,
                permission: ['system_integrations']
            },
            {
                // Identity Management Integration
                action: t('Common.configure'),
                description: t('Configure.identity_management_description'),
                disabled: false,
                icon: <ExtensionOutlined sx={{ color: 'icon.primary' }} />,
                key: 'identity_management_setup',
                name: t('Sidebar.id_management_integration'),
                page: `${baseAdminRoute}/identityManagement/configuration`,
                permission: ['system_integrations']
            },
            {
                action: t('Common.go'),
                description: t('Integrations.saasOps.description'),
                disabled: false,
                icon: <IconWithVariant variant="primary" src="Insights" />,
                key: 'saas_ops_integrations',
                name: t('Integrations.saasOps'),
                page: `${baseAdminRoute}/saasOpsIntegrations`,
                permission: ['system_permissions']
            }
        ];

        // Should this be based on the tenant you're viewing and not the session?
        if (sessionIsAnm) {
            options.push({
                action: t('Common.go'),
                description: t('Utilities.manage_users.description'),
                disabled: false,
                icon: <People sx={{ color: 'icon.primary' }} />,
                key: 'manage_users',
                // Manage Users
                name: t('Common.manage_users'),
                page: `${baseAdminRoute}/manageUsers/users`,
                permission: ['system_permissions']
            });
            options.push({
                action: t('Common.configure'),
                description: t('Configure.permissions'),
                disabled: false,
                icon: <Lock sx={{ color: 'icon.primary' }} />,
                key: 'system_permissions_card',
                // Permissions
                name: t('Sidebar.permissions'),
                page: `${baseAdminRoute}/permissions`,
                permission: ['system_permissions']
            });
        }

        options = options.sort((a, b) => a.name.localeCompare(b.name));

        return options;
    }, [baseAdminRoute, sessionIsAnm, t]);

    const renderRow = (row) => (
        <>
            <TableCell>
                <Box display="flex" alignItems="center">
                    <Box mr="4px">{row.icon}</Box>
                    <Box>{row.name}</Box>
                </Box>
            </TableCell>
            <TableCell>
                <Typography
                    dangerouslySetInnerHTML={{ __html: row.description }}
                />
            </TableCell>
            <TableCell style={{ textAlign: 'right' }}>
                {row.external ? (
                    <a
                        target="_blank"
                        href={row.page}
                        rel="noopener noreferrer nofollow"
                        data-trackid={`admin_utilities_${row.action}_routeTo`}
                    >
                        <Button
                            data-trackid={`admin_utilities_${row.action}`}
                            variant="contained"
                            color="primary"
                            size="anmMedium"
                        >
                            {row.action}
                        </Button>
                    </a>
                ) : (
                    <Link
                        to={row.page}
                        data-trackid={`admin_utilities_${row.action}_routeTo`}
                    >
                        <Button
                            data-trackid={`platformUtilities_${row.name}`}
                            variant="contained"
                            color="primary"
                            size="anmMedium"
                        >
                            {row.action}
                        </Button>
                    </Link>
                )}
                {row.disabled && (
                    <Typography size="small">
                        {t('Common.coming_soon')}
                    </Typography>
                )}
            </TableCell>
        </>
    );

    return (
        <AdminView page="configure">
            <PageTitle title={t('Configure.title')} />
            <Paper elevation={0} variant="outlined">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <ManagedTable
                            headers={headers}
                            renderBody={renderRow}
                            renderBodyKey="name"
                            renderFooter={false}
                            rows={utilitiesOptions}
                            rowsPerPage={-1}
                            searchEnabled
                            searchFields={searchFields}
                            sortDirection="ASC"
                            sortFieldType="string"
                            sortKey="name"
                        />
                    </Grid>
                </Grid>
            </Paper>
        </AdminView>
    );
};
